import Controller from '@ember/controller'
import { tracked } from '@glimmer/tracking'
import { service } from '@ember/service'
import type BookLibraryCollectionService from 'teacher-ui-client/services/book-library/collection'
import type { IntlService } from 'ember-intl'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type { Log } from '@blakeelearning/log'
import type BookLibraryCollection from 'teacher-ui-client/models/book-library/collection'

export default class CollectionIndexController extends Controller {
  @service('book-library/collection')
  collectionService!: BookLibraryCollectionService

  @service
  intl!: IntlService

  @service
  flashQueue!: FlashQueueService

  @service
  log!: Log

  @tracked
  deleteCandidate?: BookLibraryCollection = undefined

  openDeleteCollectionModal = (collection: BookLibraryCollection) => {
    this.deleteCandidate = collection
  }

  closeDeleteCollectionModal = () => {
    this.deleteCandidate = undefined
  }

  get collections() {
    return this.collectionService.allMyCollections.filter((collection) => !collection.isNew)
  }

  // Split collections into rows for the view
  get collectionsView() {
    const { collections } = this

    const sortedCollections = collections.slice().sort((a, b) => {
      // My favourites collection always first
      if (a.isFavourite) return -1
      if (b.isFavourite) return 1
      // Sort by most recent at the start
      if (a.lastUpdated === b.lastUpdated) return 0
      return a.lastUpdated > b.lastUpdated ? -1 : 1
    })

    // If only a few collections, just show on one row
    if (sortedCollections.length < 4) {
      return { firstRow: sortedCollections }
    } else {
      // split into 2 rows.
      const halfLength = sortedCollections.length / 2
      return {
        firstRow: sortedCollections.slice(0, halfLength),
        secondRow: sortedCollections.slice(halfLength),
      }
    }
  }

  get bookCount() {
    return this.collections?.reduce((sum, collection) => {
      return sum + collection.count
    }, 0)
  }

  get isShowingDeleteModal() {
    return Boolean(this.deleteCandidate)
  }

  onDeleteCollectionSubmit = async (collection: BookLibraryCollection) => {
    const { collectionService, onDeleteCollectionSuccess, onDeleteCollectionError } = this
    const { ok } = await collectionService.destroyCollection.perform(collection)
    if (ok) {
      onDeleteCollectionSuccess()
    } else {
      onDeleteCollectionError()
    }
  }

  onCollectionCreateSuccess = (_collection: BookLibraryCollection) => {
    this.flashQueue.addSuccess({ subtitle: this.intl.t('bookLibrary.collections.actions.create.success') })
  }

  onCollectionCreateFail = (_collection: BookLibraryCollection, error: unknown) => {
    this.flashQueue.addFail({ subtitle: this.intl.t('bookLibrary.collections.actions.create.fail') })
    this.log.error('book library collection action failed', error ?? undefined)
  }

  onDeleteCollectionSuccess = () => {
    const message = this.intl.t('bookLibrary.collection.delete.success')
    this.flashQueue.addSuccess({ subtitle: message })
    this.closeDeleteCollectionModal()
  }

  onDeleteCollectionError = () => {
    const message = this.intl.t('bookLibrary.collection.delete.error', { htmlSafe: true })
    this.flashQueue.addFail({ subtitle: message })
    this.closeDeleteCollectionModal()
  }

  onDeleteCollectionCancel = () => {
    this.closeDeleteCollectionModal()
  }
}
